import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Servicio para manejar operaciones de Arriendos
 * Encapsula todas las llamadas al API del módulo de arriendos
 *
 * Este servicio encapsula todas las llamadas al API relacionadas con
 * la gestión de propiedades y locales arrendados por la empresa.
 */
class ArriendosService {
    /**
     * Configurar headers de autenticación
     */
    _getHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    /**
     * Configurar headers para multipart/form-data
     */
    _getMultipartHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        };
    }

    /**
     * Manejar errores de API
     */
    _handleError(error, defaultMessage) {
        if (error.response) {
            throw new Error(error.response.data.error || error.response.data.message || defaultMessage);
        } else if (error.request) {
            throw new Error('No se pudo conectar con el servidor');
        } else {
            throw new Error(error.message || defaultMessage);
        }
    }

    /**
     * Obtener arriendos con filtros opcionales
     *
     * @param {Object} params - Parámetros de filtro
     * @param {number} params.id_company - ID de la empresa (requerido)
     * @param {string} params.estado - Estado del arriendo (opcional)
     * @param {number} params.anio - Año para filtrar (opcional)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de arriendos
     */
    async getArriendos(params, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/arriendos`,
                {
                    params,
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener arriendos');
        }
    }

    /**
     * Obtener un arriendo por ID
     *
     * @param {number} id - ID del arriendo
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Datos del arriendo
     */
    async getArriendoById(id, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/arriendos/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener arriendo');
        }
    }

    /**
     * Crear un nuevo arriendo
     *
     * @param {Object} arriendoData - Datos del arriendo
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Arriendo creado
     */
    async createArriendo(arriendoData, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/arriendos`,
                arriendoData,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al crear arriendo');
        }
    }

    /**
     * Actualizar un arriendo existente
     *
     * @param {number} id - ID del arriendo
     * @param {Object} arriendoData - Datos actualizados del arriendo
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Arriendo actualizado
     */
    async updateArriendo(id, arriendoData, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/arriendos/${id}`,
                arriendoData,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar arriendo');
        }
    }

    /**
     * Eliminar un arriendo
     *
     * @param {number} id - ID del arriendo
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la operación
     */
    async deleteArriendo(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/arriendos/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar arriendo');
        }
    }

    /**
     * Cambiar estado de un arriendo
     *
     * @param {number} id - ID del arriendo
     * @param {Object} data - Datos del cambio de estado
     * @param {number} data.id_company - ID de la empresa
     * @param {string} data.estado - Nuevo estado (activo, terminado, suspendido)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Arriendo actualizado
     */
    async cambiarEstado(id, data, token) {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/arriendos/${id}/estado`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al cambiar estado del arriendo');
        }
    }

    /**
     * Obtener resumen de arriendos por año
     *
     * @param {number} anio - Año para el resumen
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resumen de arriendos
     */
    async getResumen(anio, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/arriendos/resumen/${anio}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener resumen de arriendos');
        }
    }

    // ============================================
    // PAGOS MENSUALES
    // ============================================

    /**
     * Obtener todos los pagos mensuales de un arriendo para un año específico
     *
     * Este método:
     * - Retorna los 12 meses del año con su estado de pago
     * - Crea automáticamente registros de pago pendientes si no existen
     * - Incluye información del arriendo base
     *
     * @param {number} id_arriendo - ID del arriendo
     * @param {number} anio - Año de los pagos a consultar
     * @param {number} id_company - ID de la empresa (requerido para seguridad)
     * @param {string} token - Token JWT de autenticación
     * @returns {Promise<Object>} Objeto con: { arriendo: {...}, pagos: [{mes, mes_nombre, monto, estado, ...}, ...] }
     *
     * @example
     * const data = await getPagosArriendo(5, 2025, 1, token);
     * // Retorna: { arriendo: {...}, pagos: [{ mes: 1, mes_nombre: 'Enero', monto: 500000, estado: 'pagado', ... }, ...] }
     */
    async getPagosArriendo(id_arriendo, anio, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/arriendos/${id_arriendo}/pagos/${anio}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener pagos del arriendo');
        }
    }

    /**
     * Actualizar el estado y datos de un pago mensual
     *
     * Permite actualizar:
     * - Estado del pago (pagado/pendiente)
     * - Monto específico del mes
     * - Fecha de pago
     * - Observaciones
     *
     * @param {number} id_arriendo - ID del arriendo
     * @param {number} id_pago - ID del pago mensual a actualizar
     * @param {Object} pagoData - Datos a actualizar
     * @param {number} pagoData.id_company - ID de la empresa (requerido)
     * @param {string} [pagoData.estado] - Estado: 'pagado' o 'pendiente'
     * @param {number} [pagoData.monto] - Nuevo monto del pago
     * @param {string} [pagoData.fecha_pago] - Fecha en que se realizó el pago (ISO 8601)
     * @param {string} [pagoData.observaciones] - Notas adicionales
     * @param {string} token - Token JWT de autenticación
     * @returns {Promise<Object>} Pago actualizado
     *
     * @example
     * await actualizarPago(5, 123, {
     *   id_company: 1,
     *   estado: 'pagado',
     *   fecha_pago: '2025-01-15',
     *   observaciones: 'Pagado con transferencia'
     * }, token);
     */
    async actualizarPago(id_arriendo, id_pago, pagoData, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/arriendos/${id_arriendo}/pagos/${id_pago}`,
                pagoData,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar pago');
        }
    }

    /**
     * Cambiar solo el monto de un pago específico
     *
     * Útil cuando el valor del arriendo aumenta para un mes específico
     * sin cambiar el estado del pago.
     *
     * @param {number} id_arriendo - ID del arriendo
     * @param {number} id_pago - ID del pago a modificar
     * @param {number} monto - Nuevo monto del pago
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token JWT de autenticación
     * @returns {Promise<Object>} Pago actualizado
     *
     * @example
     * await cambiarMontoPago(5, 123, 550000, 1, token);
     * // Actualiza el monto de ese mes específico a $550.000
     */
    async cambiarMontoPago(id_arriendo, id_pago, monto, id_company, token) {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/arriendos/${id_arriendo}/pagos/${id_pago}/monto`,
                { monto, id_company },
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al cambiar monto del pago');
        }
    }

    // ============================================
    // EXTRACCIÓN DE DATOS CON IA
    // ============================================

    /**
     * Extraer datos de contrato de arriendo desde PDF usando Claude AI
     *
     * Este método usa inteligencia artificial para extraer automáticamente
     * información de contratos de arriendo en formato PDF, facilitando
     * la carga rápida de nuevos arriendos.
     *
     * @param {File} pdfFile - Archivo PDF del contrato
     * @param {string} token - Token JWT de autenticación
     * @returns {Promise<Object>} Objeto con los datos extraídos
     *
     * Estructura de retorno:
     * {
     *   success: boolean,
     *   data: {
     *     direccion: string,
     *     rol_bien_raiz: string,
     *     comuna: string,
     *     region: string,
     *     ... (todos los campos del arriendo)
     *   },
     *   fieldsExtracted: string[],  // Campos que se pudieron extraer
     *   fieldsMissing: string[],    // Campos que NO se pudieron extraer
     *   message: string
     * }
     *
     * @example
     * const file = document.getElementById('pdfInput').files[0];
     * const result = await extractFromPdf(file, token);
     * if (result.success) {
     *   console.log('Datos extraídos:', result.data);
     *   console.log('Campos encontrados:', result.fieldsExtracted);
     *   console.log('Campos faltantes:', result.fieldsMissing);
     * }
     */
    async extractFromPdf(pdfFile, id_company, token) {
        try {
            // Crear FormData para enviar el archivo
            const formData = new FormData();
            formData.append('pdf', pdfFile);
            formData.append('id_company', id_company);

            const response = await axios.post(
                `${API_BASE_URL}/api/arriendos/extract-from-pdf`,
                formData,
                { headers: this._getMultipartHeaders(token) }
            );

            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al extraer datos del PDF');
        }
    }
}

const arriendosService = new ArriendosService();
export default arriendosService;
